<template>
  <section v-if="loaded">
    <!-- HEADER WITH CONTROLS -->
    <v-toolbar>
      <h1>Display agenda settings</h1>
      <v-spacer />
      <v-btn color="secondary" @click="resetDisplaySettings">
        Reset settings <v-icon dark right>clear</v-icon>
      </v-btn>
      <v-btn color="success" @click="saveDisplaySettings" :disabled="!isWriteAvailable">
        Save changes <v-icon dark right>save</v-icon>
      </v-btn>
      <v-menu offset-y :disabled="noSessionsAvailable">
        <v-btn color="primary" dark slot="activator">
          Add timeline block <v-icon dark right>add</v-icon>
        </v-btn>
        <v-list>
          <v-list-tile
            v-for="(block, i) in timelineBlockTypes"
            :key="`block-${i}`"
            @click="addTimelineBlock(block.locationType)"
          >
            <v-list-tile-title>
              {{ block.title }}
            </v-list-tile-title>
          </v-list-tile>
        </v-list>
      </v-menu>
    </v-toolbar>
    <!-- HEADER WITH CONTROLS -->

    <!-- DATE -->
    <v-tabs dark show-arrows centered>
      <v-tabs-slider color="red"></v-tabs-slider>
      <v-tab
        v-for="(date, i) in Object.keys(agenda)"
        :key="`date-tab-${i}`"
        @click="selectDate(date)"
      >
        {{ divideWithDot(date) }}
      </v-tab>
    </v-tabs>
    <!-- DATE -->

    <!-- TIMELINE BLOCKS -->
    <v-alert
      v-if="noSessionsAvailable && !agenda[currentDate].length"
      type="error"
      :value="true"
    >
      No sessions availible
    </v-alert>
    <drag
      v-else
      :list="agenda[currentDate]"
      :options="dragOptions"
      class="timeline"
    >
      <v-card
        v-for="(block, i) in agenda[currentDate]"
        :key="`timeline-block-${i}`"
        :class="`timeline-block timeline-block_${block.locationType}`"
      >

        <!-- UNRELATED TO LOCATION -->
        <div
          v-if="block.locationType === 'unrelated'"
          class="timeline-block__inner"
        >
          <div class="timeline-block__body">
            <span class="dragable-icon"><v-icon>swap_vert</v-icon></span>
            <span v-if="block.sessions">{{ getSessionById(block.sessions).title || getSessionById(block.sessions).metadata.featuredOptions.title || 'No name' }}</span>
          </div>
          <v-divider></v-divider>
          <div class="timeline-block__controls">
            <v-menu offset-y>
              <v-btn color="primary" small slot="activator">
                <span v-if="!block.sessions">
                  Add session <v-icon dark right small class="icon-fix-position">add</v-icon>
                </span>
                <span v-else>
                  Edit session
                  <v-icon dark right small class="icon-fix-position">edit</v-icon>
                </span>
              </v-btn>
              <v-list>
                <v-list-tile
                  v-for="session in posibleSessionsForUnrelated"
                  :key="session.id"
                  @click="addSessionToUnrelated(i, session.id)"
                >
                  <v-list-tile-title>
                    {{ getSessionSelectionTitle(session) }}
                  </v-list-tile-title>
                </v-list-tile>
              </v-list>
            </v-menu>
            <v-btn color="error" small @click="removeTimelineBlock(i)">Delete</v-btn>
          </div>
        </div>
        <!-- UNRELATED TO LOCATION -->

        <!-- RELATED TO LOCATION -->
        <div
          v-else
          class="timeline-block__inner"
        >
          <div class="timeline-block__body">
            <div class="locations">
              <span class="dragable-icon"><v-icon>swap_vert</v-icon></span>
              <div class="location-list">
                <v-card
                  v-for="(location, j) in Object.keys(block.sessions)"
                  :key="`timeline-block_related-${j}`"
                  class="location"
                  :style="{ order: locationOrder(location) }"
                >
                  <v-card-title light class="blue-grey white--text">
                    <h3>{{ getLocationNameById(location) }}</h3>
                    <v-spacer />
                    <v-menu offset-y :class="disableMenuClass(location)">
                      <v-btn :disabled="!possibleSession(location)" color="primary" small slot="activator">
                      <span>
                        Add session <v-icon dark right small class="icon-fix-position">add</v-icon>
                      </span>
                      </v-btn>
                      <v-list>
                        <v-list-tile
                          v-for="session in posibleSessionsForLocation(location)"
                          :key="session.id"
                          @click="addSessionToRelated(i, getLocationById(location).id, session.id)"
                        >
                          <v-list-tile-title>
                          <span>
                            {{ getSessionSelectionTitle(session) }}
                          </span>
                          </v-list-tile-title>
                        </v-list-tile>
                      </v-list>
                    </v-menu>
                  </v-card-title>
                  <v-card-text>
                    <drag :list="block.sessions[location]" :options="dragOptions">
                      <v-card
                        v-for="(session, k) in block.sessions[location]"
                        :key="`${location}-session-${k}`"
                        class="session"
                      >
                        <div class="session__body">
                          <span class="dragable-icon"><v-icon>swap_vert</v-icon></span>
                          <div v-if="session.id !== 'empty'">
                            <div>Title: {{ path(['title'], getSessionById(session.id)) }}</div>
                            <div>Type: {{ path(['metadata', 'sessionType'], getSessionById(session.id)) }}</div>
                            <div>Start: {{ path(['startTime'], getSessionById(session.id)) }}</div>
                          </div>
                          <div v-else>Empty</div>
                        </div>
                        <v-divider />
                        <div>
                          <v-btn color="error" small
                                 @click="removeSessionFromRelated(i, getLocationById(location).id, k)"
                          >
                            Delete
                          </v-btn>
                        </div>
                      </v-card>
                    </drag>
                  </v-card-text>
                </v-card>
              </div>
            </div>
          </div>
          <v-divider />
          <div class="timeline-block__controls">
            <v-btn color="error" small @click="removeTimelineBlock(i)">Delete</v-btn>
          </div>
        </div>
        <!-- RELATED TO LOCATION -->
      </v-card>
    </drag>
    <!-- TIMELINE BLOCKS -->
  </section>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { isNil, clone, path, pathOr } from 'ramda'
import moment from 'moment' // lib to format date and time
import mixins from '@/utils/mixins'

// TODO: make code clean!!!

export default {
  name: 'AgendaSettings',
  mixins: [mixins.isWriteAvailable],
  data () {
    return {
      loaded: false,
      dragOptions: {
        ghostClass: 'before-drag-drop'
      },
      currentDate: '',
      agenda: {},
      sessions: null,
      locations: null,
      // DON'T DELETE! need for isWriteAvailable mixin
      scopeForWrite: 'agenda:write'
    }
  },

  computed: {
    ...mapGetters([
      'sessionsList',
      'locationsList',
      'getSettings'
    ]),
    agendaSettings () {
      return path(['agendaSetting'], this.getSettings)
    },

    noSessionsAvailable () {
      return !this.posibleSessionsForUnrelated.length && !this.existsUnusedRelatedSession
    },

    timelineBlockTypes () {
      // related or unrelated to location blocks posible to add in agenda timeline
      let types = []

      if (this.posibleSessionsForUnrelated.length) {
        types.push({ locationType: 'unrelated', title: 'Unrelated to location' })
      }

      if (this.existsUnusedRelatedSession) {
        types.push({ locationType: 'related', title: 'Related to location' })
      }

      return types
    },

    posibleSessionsForUnrelated () {
      let allowed = this.sessions.filter(session => {
        return isNil(session.location) && (this.formatDate(session.startTime) === this.currentDate)
      })
      // filter used already sessions
      const sessionBlocks = pathOr([], [this.currentDate], this.agenda)
      sessionBlocks.forEach(block => {
        if (block.locationType === 'unrelated') {
          if (allowed.find(session => session.id === block.sessions)) {
            allowed = allowed.filter(session => session.id !== block.sessions)
          }
        }
      })

      return allowed
    },

    locationPosibleSessionsMap () {
      return this.sessions.reduce((map, session) => {
        if (session.location) {
          map[session.location.id]
            ? map[session.location.id] = [...map[session.location.id], session]
            : map[session.location.id] = [session]
        }
        return map
      }, {})
    },

    existsUnusedRelatedSession () {
      // if `false` hide 'Add related timeline block' button
      let result = false

      Object.keys(this.locationPosibleSessionsMap).forEach(locationId => {
        if (this.posibleSessionsForLocation(locationId).filter(session => session.id !== 'empty').length) {
          result = true
          return
        }
      })

      return result
    }
  },

  methods: {
    ...mapActions([
      'getSessionsList',
      'getLocationsList',
      'editSettings',
      'fetchGeneralSettings'
    ]),
    moment,
    path,

    locationOrder (locationId) {
      if (location.id && this.locations && this.locations.find((item) => item.id === locationId)) {
        return this.locations.filter(location => location.id === locationId)[0].order
      }
    },

    divideWithDot (date) {
      return date.split('-').join('.')
    },

    formatDate (date) {
      return moment(date).utc().format('DD-MM-YYYY')
    },

    getTime (date) {
      return moment(date).utc().format('LT')
    },

    selectDate (date) {
      this.currentDate = date
    },

    setAgendaSettingsFrom (settings) {
      const startDate = moment(settings.eventConfiguration.startDate)
      const endDate = moment(settings.eventConfiguration.endDate)
      const eventDates = this.getDatesBetween(startDate, endDate)

      eventDates.forEach(date => {
        // We add new key with date to agenda
        // using $set to tell Vue - 'hey, Vue, watch this key',
        // otherwise agenda[date] will be not reactive.
        const blocks = path(['displaySettingsObject', date, 'length'] ,settings)
          ? settings.displaySettingsObject[date]
          : []
        this.$set(this.agenda, date, blocks)
      })

      this.currentDate = eventDates[0]
      this.checkForDeletedSessionOrLocation()
      this.loaded = true
    },

    checkForDeletedSessionOrLocation () {
      let result = clone(this.agenda)
      Object.keys(this.agenda).forEach(date => {
        this.agenda[date].forEach((block, i) => {
          const savedIndex = block.order
          if (
            block.locationType === 'unrelated' &&
            (!this.getSessionById(block.sessions) ||
            path(['status'], this.getSessionById(block.sessions) === 'Deleted'))
          ) {
            const indexOfDeletable = result[date].indexOf(result[date].find(item => item.sessions === block.sessions))
            result[date].splice(indexOfDeletable, 1)
          }

          if (block.locationType === 'related') {
            Object.keys(block.sessions).forEach(location => {
              if (!this.getLocationById(location)) {
                delete block.sessions[location]
              } else {
                block.sessions[location].forEach((session, j) => {
                  if (
                    session.id !== 'empty' && (
                      !this.getSessionById(session.id) ||
                      path(['status'], this.getSessionById(session.id)) === 'Deleted' ||
                      this.hasSessionChangeLocation(location, session.id)
                    )
                  ) {
                    const indexOfBlock = result[date].indexOf(result[date].find(item => item.order === savedIndex))
                    const sessionsOfCurrentLocation = result[date][indexOfBlock].sessions[location]
                    const indexOfDeletable = sessionsOfCurrentLocation.findIndex((el)=> el.id === session.id)
                    sessionsOfCurrentLocation.splice(indexOfDeletable, 1)
                  }
                })
              }
            })
          }
        })
      })

      this.agenda = clone(result)
      this.saveDisplaySettings()
    },

    hasSessionChangeLocation (locationId, sessionId) {
      if (!this.locationPosibleSessionsMap[locationId]) {
        console.error('There is no location ', locationId)
        return true
      }

      return !this.locationPosibleSessionsMap[locationId]
        .find(possibleSession => possibleSession.id === sessionId)
    },

    getDatesBetween(startDate, endDate) {
      let now = startDate.clone(), dates = []

      while (now.isSameOrBefore(endDate)) {
        dates.push(this.formatDate(now))
        now.add(1, 'days')
      }

      if (!dates.length) {
        console.error('Invalid event dates!')
        return [startDate]
      } else {
        return dates
      }
    },

    getSessionById (id) {
      try {
        return this.sessions.find(session => session.id === id)
      } catch (error) {
        console.error(`Couldn't find session with ID:`, id)
        throw error
      }
    },

    getLocationById (id) {
      try {
        return this.locations.find(location => location.id === id)
      } catch (error) {
        console.error(`Couldn't find location with ID:`, id)
        throw error
      }
    },

    posibleSessionsForLocation (locationId) {
      if (this.locationPosibleSessionsMap[locationId]) {
        let allowed =
          this.locationPosibleSessionsMap[locationId]
          .filter(session => this.formatDate(session.startTime) === this.currentDate)

        // filter used already sessions
        const sessionBlocks = pathOr([], [this.currentDate], this.agenda)
        sessionBlocks.forEach(block => {
          if (block.locationType === 'related') {
            const sessionsInLocation = pathOr([], ['sessions', locationId], block)
            sessionsInLocation.forEach(session => {
              const alreadyUsed = allowed.find(allowedSession => session.id === allowedSession.id)
              if (alreadyUsed) {
                allowed = allowed.filter(allowedSession => allowedSession.id !== alreadyUsed.id)
              }
            })
          }
        })

        // return [{id: 'empty'}, ...allowed]
        return [...allowed]
      } else {
        // console.error('Location', locationId, 'has to posible sessions!')
        return []
      }
    },

    possibleSession (locationId) {
      return this.posibleSessionsForLocation(locationId).length > 0
    },

    disableMenuClass (locationId) {
      return this.possibleSession(locationId) ? '' : 'disabled'
    },

    getLocationsForRelatedBlock () {
      // prepare posible location columns for location related block
      console.log('locationPosibleSessionsMap', this.locationPosibleSessionsMap)
      return Object.keys(this.locationPosibleSessionsMap).reduce((acc, locationId) => {
        if (!acc[locationId]) {
          acc[locationId] = []
        }
        return acc
      }, {})
    },

    addTimelineBlock (locationType) {
      let newBlock = {
        locationType,
        order: 0
      }

      newBlock.sessions = (locationType === 'related')
        ? this.getLocationsForRelatedBlock()
        : ''

      this.agenda[this.currentDate] = clone([...this.agenda[this.currentDate], newBlock])
    },

    removeTimelineBlock (index) {
      this.agenda[this.currentDate] = this.agenda[this.currentDate].filter((block, i) => i !== index)
    },

    saveDisplaySettings () {
      // save new order of blocks and sessions
      this.agenda[this.currentDate].map((block, i) => {
        block.order = i
        if (block.locationType === 'related') {
          Object.keys(block.sessions).map((sessionName, j) => {
            block.sessions[sessionName].map((session, k) => {
              session.order = k
            })
          })
        }
      })
      this.sendDisplaySetting(this.agenda)
    },

    resetDisplaySettings () {
      for (let key in this.agenda) {
        this.agenda[key] = []
      }
      this.sendDisplaySetting({})
    },

    async sendDisplaySetting (settings) {
      try {
        const payload = clone(this.agendaSettings)
        payload.displaySettingsObject = clone(settings)
        await this.editSettings(payload)
        await this.fetchGeneralSettings()
      } catch (err) {
        console.error(err)
      }
    },

    addSessionToUnrelated (blockIndex, sessionId) {
      this.agenda[this.currentDate][blockIndex].sessions = sessionId
    },

    addSessionToRelated (blockIndex, locationId, sessionId) {
      const location = this.agenda[this.currentDate][blockIndex].sessions[locationId]
      const isSessionAdded = location.find(session => (session.id === sessionId))
      const isEmpty = sessionId === 'empty'
      if (isEmpty || isSessionAdded) return
      const newSession = { id: sessionId, order: location.length }
      this.agenda[this.currentDate][blockIndex].sessions[locationId] = [
        ...location,
        newSession
      ]
    },

    removeSessionFromRelated (blockIndex, locationId, sessionIndex) {
      this.agenda[this.currentDate][blockIndex].sessions[locationId] =
        this.agenda[this.currentDate][blockIndex].sessions[locationId]
        .filter((session, index) => index !== sessionIndex)
    },

    getSessionSelectionTitle (sessionInfo) {
      return `
        ${sessionInfo.title || sessionInfo.metadata.featuredOptions.title || 'No name'} |
        ${this.getTime(sessionInfo.startTime)} |
        ${sessionInfo.metadata.sessionType}
      `
    },

    getLocationNameById (location) {
      return path(['name'], this.getLocationById(location))
    }
  },

  async created () {
    moment.locale('en-gb')
    await this.getSessionsList({fetchAll: true})
    this.sessions = [...this.sessionsList.filter(session => session.status === 'Publish')]
    await this.getLocationsList({fetchAll: true})
    this.locations = [...this.locationsList]
    if (!this.getSettings) {
      this.fetchGeneralSettings()
    }
    this.setAgendaSettingsFrom(this.agendaSettings)
  }
}
</script>

<style lang="scss" scoped>
  /deep/ .menu--disabled {
    opacity: 0.3;
  }

  /deep/ .btn {
    margin-right: 0;
  }

  .menu {
    &.disabled {
      pointer-events: none;
    }
  }

  .timeline {
    margin-top: 20px;

    &-block {
      background: #f5f5f5;
      display: flex;
      margin: 10px 0;
      padding: 10px 15px;
      cursor: move; /* fallback if grab cursor is unsupported */
      cursor: grab;

      &__body,
      &__controls {
        padding: 10px 0;
      }

      &__inner {
        width: 100%;
      }
    }
  }

  .locations {
    display: flex;
    flex: 1;
  }

  .location-list {
    display: flex;
    justify-content: space-between;
    width: 100%;
  }

  .location {
    cursor: default;
    flex: 1;
    background: #e8eaf6;
    flex-basis: 33%;
    flex-grow: 0;
  }

  .session {
    cursor: move;
    cursor: grab;
    padding: 5px;
    padding-right: 20px;
    margin-bottom: 10px;
    min-height: 114px !important;
    overflow: hidden;

    &__body {
      display: flex;
    }

    &:last-child {
      margin-bottom: 0;
    }
  }

  .dragable-icon {
    margin-right: 10px;
  }

  .before-drag-drop {
    opacity: 0.35;
    background: lightgray;
  }

  .icon-fix-position {
    margin-top: -3px;
  }
</style>



// WEBPACK FOOTER //
// src/components/pages/Agenda/Settings/index.vue